import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { clsx } from "clsx";

type Variant = "primary" | "secondary" | "ghost" | "dark";
type Size = "sm" | "md" | "lg";

const variantClasses: Record<Variant, string> = {
  primary: "bg-brand-accent text-white shadow-glow hover:bg-[#d9781e]",
  secondary: "border border-brand-line bg-white/70 text-brand-text hover:bg-white",
  ghost: "text-brand-text hover:bg-white/60",
  dark: "bg-brand-text text-white hover:bg-brand-ink"
};

const sizeClasses: Record<Size, string> = {
  sm: "h-10 px-4 text-sm",
  md: "h-12 px-5 text-sm",
  lg: "h-14 px-6 text-base"
};

export function buttonClassName({
  variant = "primary",
  size = "md",
  className
}: {
  variant?: Variant;
  size?: Size;
  className?: string;
}) {
  return clsx(
    "focus-ring inline-flex items-center justify-center gap-2 rounded-brand font-bold transition duration-200 disabled:opacity-55",
    variantClasses[variant],
    sizeClasses[size],
    className
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: ReactNode;
}

export function Button({ className, variant, size, icon, children, ...props }: ButtonProps) {
  return (
    <button className={buttonClassName({ variant, size, className })} {...props}>
      {icon}
      {children}
    </button>
  );
}

interface LinkButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  variant?: Variant;
  size?: Size;
  showArrow?: boolean;
}

export function LinkButton({
  to,
  className,
  variant,
  size,
  children,
  showArrow = false,
  ...props
}: LinkButtonProps) {
  return (
    <Link className={buttonClassName({ variant, size, className })} to={to} {...props}>
      {children}
      {showArrow ? <ArrowRight aria-hidden="true" size={18} /> : null}
    </Link>
  );
}
